import { isPlainObject } from './profile';
import type { EmptyReason, ExtractionSource, UploadedCandidate, UploadedStandard, UploadedStandardRow } from './types';

/**
 * The rules for a teacher's own standards. Pure, and imported by the browser bundle as well
 * as the Worker: the CSV a teacher chooses is parsed here before it ever leaves the page,
 * and the rows the Worker saves pass through the same `normalizeRows`.
 */

export const MAX_UPLOADED_ROWS = 2000;
export const MAX_CODE_LENGTH = 64;
export const MAX_STATEMENT_LENGTH = 2000;
const MAX_SUBJECT_LENGTH = 100;
const MAX_GRADE_LENGTH = 20;
const MAX_GRADES = 20;
/** At most this many of the teacher's own standards join the Learning Commons candidates. */
export const MAX_UPLOADED_CANDIDATES = 10;

// ---------------------------------------------------------------------------------------
// CSV

export type CsvProblem = {
  line: number;
  reason: 'empty_file' | 'missing_code_column' | 'missing_statement_column' | 'missing_code' | 'missing_statement';
};
export interface CsvParse { rows: UploadedStandardRow[]; problems: CsvProblem[] }

type Column = 'code' | 'statement' | 'grade' | 'subject';
/** The names districts and spreadsheet exports actually use, compared after lowercasing. */
export const HEADER_ALIASES: Record<Column, readonly string[]> = {
  code: ['code', 'standard code', 'id', 'statement code'],
  statement: ['statement', 'description', 'standard', 'text'],
  grade: ['grade', 'grades', 'grade level', 'grade levels'],
  subject: ['subject', 'academic subject'],
};

/**
 * RFC 4180 records, each with the line it starts on so a problem can point at it.
 *
 * A quote opens a quoted field only at the start of a field (after any spaces). A stray quote inside an
 * unquoted field (`5" ruler`) is kept as text, which is what a teacher meant by it.
 */
function csvRecords(text: string): { fields: string[]; line: number }[] {
  const records: { fields: string[]; line: number }[] = [];
  let fields: string[] = [];
  let field = '';
  let quoted = false;
  let wasQuoted = false;
  let line = 1;
  let start = 1;
  const endField = () => { fields.push(field); field = ''; wasQuoted = false; };
  for (let i = 0; i < text.length; i++) {
    const ch = text[i]!;
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') quoted = false;
      else { if (ch === '\n') line++; field += ch; }
    } else if (ch === '"' && field.trim() === '' && !wasQuoted) {
      // Hand-written files put a space after the comma; `a, "b, c"` still means two fields.
      field = '';
      quoted = true;
      wasQuoted = true;
    } else if (ch === ',') {
      endField();
    } else if (ch === '\r' || ch === '\n') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      endField();
      records.push({ fields, line: start });
      fields = [];
      line++;
      start = line;
    } else {
      field += ch;
    }
  }
  if (field !== '' || wasQuoted || fields.length > 0) { endField(); records.push({ fields, line: start }); }
  return records;
}

const headerKey = (name: string) => name.trim().toLowerCase().replace(/[\s_]+/g, ' ');

/** A row as stored and sent: grades and subject appear only when there is one to give. */
export function toUploadedRow(code: string, statement: string, gradeLevels?: ReadonlyArray<string>, subject?: string | null): UploadedStandardRow {
  return { code, statement, ...(gradeLevels?.length ? { gradeLevels } : {}), ...(subject ? { subject } : {}) };
}

/** Grades as the file wrote them. Matching them to a profile is `withinProfileGrades`' job. */
function splitGrades(value: string): string[] {
  return [...new Set(value.split(/[,;|]/).map((g) => g.trim()).filter(Boolean))];
}

export function parseStandardsCsv(text: string): CsvParse {
  const records = csvRecords(text.replace(/^\uFEFF/, '')).filter((r) => r.fields.some((f) => f.trim() !== ''));
  const header = records[0];
  if (!header) return { rows: [], problems: [{ line: 1, reason: 'empty_file' }] };
  const keys = header.fields.map(headerKey);
  const index = (column: Column) => keys.findIndex((key) => HEADER_ALIASES[column].includes(key));
  const at = { code: index('code'), statement: index('statement'), grade: index('grade'), subject: index('subject') };
  const missing: CsvProblem[] = [
    ...(at.code < 0 ? [{ line: header.line, reason: 'missing_code_column' as const }] : []),
    ...(at.statement < 0 ? [{ line: header.line, reason: 'missing_statement_column' as const }] : []),
  ];
  if (missing.length) return { rows: [], problems: missing };

  const rows: UploadedStandardRow[] = [];
  const problems: CsvProblem[] = [];
  for (const { fields, line } of records.slice(1)) {
    const cell = (i: number) => (fields[i] ?? '').trim();
    const code = cell(at.code);
    const statement = cell(at.statement);
    if (!code) { problems.push({ line, reason: 'missing_code' }); continue; }
    if (!statement) { problems.push({ line, reason: 'missing_statement' }); continue; }
    const gradeLevels = splitGrades(cell(at.grade));
    const subject = cell(at.subject);
    rows.push(toUploadedRow(code, statement, gradeLevels, subject));
  }
  return { rows, problems };
}

// ---------------------------------------------------------------------------------------
// Row rules

export type NormalizeResult =
  | { ok: true; rows: UploadedStandardRow[] }
  | { ok: false; reason: 'invalid_rows' | 'no_rows' | 'too_many_rows' };

const clean = (value: unknown) => (typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : '');
/** Caps by code point, so a cut never leaves half a surrogate pair in storage. */
const cap = (value: string, max: number) => (value.length <= max ? value : Array.from(value).slice(0, max).join(''));

/**
 * The rows a save will store, from whatever the request carried.
 *
 * Rows without both a code and a statement are dropped rather than refused: review already
 * showed the teacher every row, and a blank line from a spreadsheet is not a mistake worth
 * failing a save over. A row that is not an object at all means the client is broken, so
 * that does fail.
 */
export function normalizeRows(input: unknown): NormalizeResult {
  if (!Array.isArray(input) || !input.every(isPlainObject)) return { ok: false, reason: 'invalid_rows' };
  const rows = input.flatMap((raw): UploadedStandardRow[] => {
    const code = cap(clean(raw.code), MAX_CODE_LENGTH);
    const statement = cap(clean(raw.statement), MAX_STATEMENT_LENGTH);
    if (!code || !statement) return [];
    const gradeLevels = Array.isArray(raw.gradeLevels)
      ? [...new Set(raw.gradeLevels.map((g) => cap(clean(g), MAX_GRADE_LENGTH)).filter(Boolean))].slice(0, MAX_GRADES)
      : [];
    const subject = cap(clean(raw.subject), MAX_SUBJECT_LENGTH);
    return [toUploadedRow(code, statement, gradeLevels, subject)];
  });
  if (rows.length === 0) return { ok: false, reason: 'no_rows' };
  if (rows.length > MAX_UPLOADED_ROWS) return { ok: false, reason: 'too_many_rows' };
  return { ok: true, rows };
}

/** Where a saved set came from. Recorded for analytics; it changes nothing about search. */
export type UploadFormat = 'csv' | 'paste' | 'pdf';
const UPLOAD_FORMATS: readonly string[] = ['csv', 'paste', 'pdf'];

export type SaveDecision =
  | { ok: true; format: UploadFormat; rows: UploadedStandardRow[] }
  | { ok: false; reason: 'invalid_request' | 'invalid_format' | Extract<NormalizeResult, { ok: false }>['reason'] };

/** A `PUT /api/standards` body: the reviewed rows and the format they started as. */
export function decideSave(input: unknown): SaveDecision {
  if (!isPlainObject(input)) return { ok: false, reason: 'invalid_request' };
  if (typeof input.format !== 'string' || !UPLOAD_FORMATS.includes(input.format)) return { ok: false, reason: 'invalid_format' };
  const rows = normalizeRows(input.rows);
  return rows.ok ? { ok: true, format: input.format as UploadFormat, rows: rows.rows } : rows;
}

/** R3 and R4. The PDF cap is on decoded bytes; its base64 is a third larger. */
export const MAX_PASTE_CHARS = 100_000;
export const MAX_PDF_BYTES = 10 * 1024 * 1024;
const BASE64 = /^[A-Za-z0-9+/]*={0,2}$/;
/** `%PDF-` in base64. Checked so a mislabelled file is refused before it costs an extraction. */
const PDF_MAGIC = 'JVBERi0';

export type ExtractionDecision =
  | { ok: true; source: ExtractionSource; format: Exclude<UploadFormat, 'csv'> }
  | { ok: false; reason: 'invalid_request' | 'text_too_long' | 'invalid_pdf' | 'pdf_too_large' };

/**
 * A `POST /api/standards/extract` body, checked before any budget is reserved, so a request
 * that could never succeed costs the teacher nothing.
 */
export function decideExtraction(input: unknown): ExtractionDecision {
  if (!isPlainObject(input)) return { ok: false, reason: 'invalid_request' };
  if (input.kind === 'text') {
    if (typeof input.text !== 'string' || !input.text.trim()) return { ok: false, reason: 'invalid_request' };
    if (input.text.length > MAX_PASTE_CHARS) return { ok: false, reason: 'text_too_long' };
    // Not whitespace-collapsed like an ask: line breaks are how a pasted list says where
    // one standard ends and the next begins.
    return { ok: true, source: { kind: 'text', data: input.text.trim() }, format: 'paste' };
  }
  if (input.kind === 'pdf') {
    const data = input.pdfBase64;
    if (typeof data !== 'string' || !data) return { ok: false, reason: 'invalid_pdf' };
    // Size first: it is arithmetic, and the format checks below walk the whole string.
    const padding = data.endsWith('==') ? 2 : data.endsWith('=') ? 1 : 0;
    if ((data.length / 4) * 3 - padding > MAX_PDF_BYTES) return { ok: false, reason: 'pdf_too_large' };
    if (data.length % 4 !== 0 || !BASE64.test(data) || !data.startsWith(PDF_MAGIC)) return { ok: false, reason: 'invalid_pdf' };
    return { ok: true, source: { kind: 'pdf', data }, format: 'pdf' };
  }
  return { ok: false, reason: 'invalid_request' };
}

/**
 * Indexes of rows whose code or statement is not in the pasted text.
 *
 * Extraction must copy, never write (R5). Comparing with all whitespace removed forgives
 * the line breaks and spacing a paste carries, and nothing else: a reworded statement or
 * an invented code is still flagged for the teacher to look at during review.
 */
export function unverifiedRows(rows: ReadonlyArray<UploadedStandardRow>, sourceText: string): number[] {
  const squeeze = (value: string) => value.replace(/\s+/g, '');
  const source = squeeze(sourceText);
  return rows.flatMap((row, i) => (source.includes(squeeze(row.code)) && source.includes(squeeze(row.statement)) ? [] : [i]));
}

// ---------------------------------------------------------------------------------------
// Search

const STOPWORDS = new Set([
  'a', 'about', 'after', 'all', 'also', 'an', 'and', 'any', 'are', 'as', 'at', 'be', 'been', 'but', 'by', 'can', 'do', 'does',
  'for', 'from', 'get', 'had', 'has', 'have', 'help', 'how', 'i', 'if', 'in', 'into', 'is', 'it', 'its', 'like', 'me', 'my',
  'need', 'next', 'not', 'of', 'on', 'or', 'our', 'over', 'so', 'some', 'student', 'students', 'teach', 'teaching', 'than',
  'that', 'the', 'their', 'them', 'then', 'there', 'these', 'they', 'this', 'those', 'to', 'up', 'us', 'want', 'was', 'we',
  'week', 'were', 'what', 'when', 'which', 'who', 'why', 'will', 'with', 'would', 'you', 'your',
]);
const MAX_TERMS = 24;
/** Letters and digits joined by dots or dashes, with a digit somewhere: `6.RP.A.1`, `K-PS2-1`. */
const CODE_SHAPE = /^[\p{L}\p{N}]+(?:[.-][\p{L}\p{N}]+)+$/u;
/** A word starts with a letter or digit; combining marks may follow so `é` stays one word. */
const WORD = /[\p{L}\p{N}][\p{L}\p{N}\p{M}]*/gu;

/**
 * A safe FTS5 `MATCH` expression for one search query, or null when nothing searchable is
 * left.
 *
 * Every term is quoted and holds only letters, digits, marks, dots and dashes, so nothing
 * the teacher or the model typed can become an operator, a column filter or a stray quote.
 * Terms are OR-joined because a district's wording rarely repeats the query's; bm25 ranks
 * the rows that share more of it. A code-shaped token searches the `code` column as a
 * phrase, so a teacher who types `6.RP.A.1` finds that row and not every row mentioning 6.
 */
export function ftsMatchExpression(query: string): string | null {
  const terms = new Set<string>();
  for (const chunk of query.split(/\s+/)) {
    const token = chunk.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '');
    if (CODE_SHAPE.test(token) && /\p{N}/u.test(token)) {
      terms.add(`code : "${token}"`);
      continue;
    }
    for (const word of token.match(WORD) ?? []) {
      const lower = word.toLowerCase();
      if (lower.length > 1 && !STOPWORDS.has(lower)) terms.add(`"${lower}"`);
    }
  }
  return terms.size ? [...terms].slice(0, MAX_TERMS).join(' OR ') : null;
}

/**
 * The `MATCH` expressions one ask searches the teacher's set with: the ask's own text, so a
 * teacher who types a code finds it, and then each of the model's search queries.
 */
export function uploadedSearchExpressions(texts: ReadonlyArray<string>): string[] {
  return [...new Set(texts.map(ftsMatchExpression).filter((e): e is string => e !== null))];
}

/**
 * The lowest `-bm25` score an uploaded hit may have and still reach the model.
 *
 * FTS5 floors the weight of any term that half or more of the stored rows contain at about
 * zero, so a hit carried only by the verbs every standard uses ("describe", "understand",
 * "use") scores near 0.0, while one shared term that fewer than about two in five rows
 * contain scores 0.5 or more even in a three-row table. The floor sits between the two.
 *
 * The statistics are over the whole table, every teacher's rows together, so a term common
 * in one district's file still weighs little once many districts are stored. A set of one or
 * two rows alone in the table cannot clear any floor: each of its terms is in half the rows.
 */
export const MIN_UPLOADED_SCORE = 0.25;

const GRADE_ORDER = ['pk', 'k', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
/** A grade as a file might write it (`Grade 7`, `06`, `7th`, `Kindergarten`) in profile terms. */
function gradeKey(raw: string): string {
  const g = raw.trim().toLowerCase().replace(/^(grade|gr\.?)\s*/, '').replace(/(st|nd|rd|th)$/, '').replace(/^0+(?=\d)/, '');
  if (g === 'kindergarten' || g === 'kg') return 'k';
  if (g === 'prek' || g === 'pre-k' || g === 'pre-kindergarten') return 'pk';
  return g;
}
/** `6-8` and `K–2` are bands; each grade inside counts. */
function gradeKeys(raw: string): string[] {
  const band = raw.match(/^(.+?)\s*[-–]\s*(.+)$/);
  if (band) {
    const from = GRADE_ORDER.indexOf(gradeKey(band[1]!));
    const to = GRADE_ORDER.indexOf(gradeKey(band[2]!));
    if (from >= 0 && to > from) return GRADE_ORDER.slice(from, to + 1);
  }
  return [gradeKey(raw)];
}

/**
 * Whether an uploaded row may answer an ask for these grades.
 *
 * A row the file gave no grade could be any grade, so it stays; so does every row when the
 * teacher gave no grade. Only a row that names grades, none of them the teacher's, goes.
 */
export function withinProfileGrades(row: { gradeLevels?: ReadonlyArray<string> }, profileGrades: ReadonlyArray<string>): boolean {
  if (!profileGrades.length || !row.gradeLevels?.length) return true;
  const wanted = new Set(profileGrades.map(gradeKey));
  return row.gradeLevels.some((g) => gradeKeys(g).some((key) => wanted.has(key)));
}

/** A stored row as a candidate the model can pick by id. */
export function uploadedCandidate(row: UploadedStandard): UploadedCandidate {
  return {
    source: 'uploaded',
    id: `upload:${row.standardId}`,
    uploadedStandardId: row.standardId,
    statementCode: row.code,
    description: row.statement,
    ...(row.gradeLevels.length ? { gradeLevels: [...row.gradeLevels] } : {}),
    ...(row.subject ? { academicSubject: row.subject } : {}),
  };
}

// ---------------------------------------------------------------------------------------
// Extraction salvage

export const EMPTY_REASONS: readonly EmptyReason[] = ['no_standards', 'standards_without_codes', 'unreadable'];

/**
 * The model's account of an empty extraction, when it gave one from the list.
 *
 * Five of the first seven extractions came back with no rows and nothing to say why. A
 * document of objectives without codes and a scan with no text look the same from here; the
 * model, which read it, can tell them apart without any of its words reaching analytics.
 */
export function emptyReasonFrom(parsed: unknown): EmptyReason | undefined {
  const reason = isPlainObject(parsed) ? parsed.empty_reason : undefined;
  return EMPTY_REASONS.find((r) => r === reason);
}

function rowFromModel(value: unknown): UploadedStandardRow | null {
  if (!isPlainObject(value) || typeof value.code !== 'string' || typeof value.statement !== 'string') return null;
  const gradeLevels = typeof value.grade === 'string' ? splitGrades(value.grade)
    : Array.isArray(value.gradeLevels) ? value.gradeLevels.filter((g): g is string => typeof g === 'string') : [];
  const subject = typeof value.subject === 'string' ? value.subject : '';
  return toUploadedRow(value.code, value.statement, gradeLevels, subject);
}

/**
 * Every complete row in a `{ "rows": [ {...}, ... ` response, however it was cut off.
 *
 * Extraction stops when the deadline or the token cap does, usually mid-row. Rather than
 * fail a long document, this walks the array one object at a time, tracking strings and
 * escapes so a `}` inside a statement does not end a row, and keeps each object that
 * closed. The first object that did not close is where the response was cut.
 */
export function salvageRows(partial: string): UploadedStandardRow[] {
  const open = /^\s*\{\s*"rows"\s*:\s*\[/.exec(partial);
  if (!open) return [];
  const rows: UploadedStandardRow[] = [];
  let i = open[0].length;
  for (;;) {
    while (i < partial.length && /[\s,]/.test(partial[i]!)) i++;
    if (partial[i] !== '{') return rows;
    const start = i;
    let depth = 0;
    let inString = false;
    let end = -1;
    for (; i < partial.length; i++) {
      const ch = partial[i];
      if (inString) {
        if (ch === '\\') i++;
        else if (ch === '"') inString = false;
      } else if (ch === '"') inString = true;
      else if (ch === '{' || ch === '[') depth++;
      else if ((ch === '}' || ch === ']') && --depth === 0) { end = i + 1; break; }
    }
    if (end < 0) return rows;
    let parsed: unknown;
    try { parsed = JSON.parse(partial.slice(start, end)); } catch { return rows; }
    const row = rowFromModel(parsed);
    if (row) rows.push(row);
    i = end;
  }
}
